import { Icon } from "@iconify/react";
import { LoginBox } from "../components/loginBox";
import { Header1, P } from "../components/headers";
import { useStateMachine } from "little-state-machine";
import { updateState } from "../littleStateMachine/updateRedirectUrl";
import userTimeZoneCountryCode from "../components/userTimeZoneCountryCode";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import userBrowserLanguage, { userBrowserLanguageChar2 } from "../components/userBrowserLanguage";
import getTranslationJson from "../components/getTranslationJson";
import React from "react";
import Input, { InputCheckbox, InputPassword } from "../components/input";
import { useForm } from "react-hook-form";
import axios, { AxiosError, type AxiosResponse } from "axios";
import { Link } from "../components/link";
import { Button } from "../components/button";
import type { components as sso } from "../hooks/sso";
import { isNotLocalUrl } from "../littleStateMachine/utils";
import { useSearchParams } from "react-router";
import { notificationGrayClass, notificationGreenClass } from "../components/style";

interface FormValues {
  email: string;
  password: string;
  confirmpassword: string;
  first_name: string;
  last_name: string;
  marketing_opt_in?: boolean;
  apiError?: string;
}

function AuthRegister() {
  const { actions, getState } = useStateMachine({
    actions: { updateState },
  });
  const [successfullNotification, setSuccessfullNotification] = React.useState(false);
  const [searchParams] = useSearchParams();

  const cookie_domain =
    searchParams.has("cookie_domain") && import.meta.env.VITE_PUBLIC_DEV === "true"
      ? searchParams.get("cookie_domain") || ""
      : import.meta.env.VITE_PUBLIC_SUNSTAR_GUM_COOKIE_DOMAIN;
  // Get the previous page (if any)
  const origin_url = searchParams.has("redirect_url")
    ? searchParams.get("redirect_url") || ""
    : document.referrer || import.meta.env.VITE_PUBLIC_SUNSTAR_GUM_URL;
  const countryCode = userTimeZoneCountryCode();
  // Access the client
  const queryClient = useQueryClient();

  const translation = getTranslationJson(userBrowserLanguage());

  const AppId = getState().loginPageData.redirectUrl.startsWith(
    import.meta.env.VITE_PUBLIC_UNITY_DEEPLINK_PREFIX_GUMPLAYBRUSH,
  )
    ? "GUMPlaybrush"
    : getState().loginPageData.redirectUrl.startsWith(import.meta.env.VITE_PUBLIC_UNITY_DEEPLINK_PREFIX_GUMSMART)
      ? "GUMSmart"
      : "GUMWebsite";

  React.useEffect(() => {
    if (isNotLocalUrl(origin_url)) {
      actions.updateState({
        loginPageData: { redirectUrl: origin_url, cookieDomain: cookie_domain },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mutations
  const mutationRegister = useMutation({
    mutationKey: ["register"],
    mutationFn: async (formData: FormValues | sso["schemas"]["RegisterFormModel"]): Promise<AxiosResponse> => {
      setSuccessfullNotification(false);
      return axios.post(import.meta.env.VITE_PUBLIC_PLATFORM_SSO_API_URL! + "/register?app_id=" + AppId, formData, {
        headers: {
          "Content-Type": "application/json",
        },
      });
    },
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: ["login"] });
      setSuccessfullNotification(true);
    },
    onError: (data: AxiosError) => {
      // Invalidate and refetch
      console.error("Login error:", data);
      // queryClient.invalidateQueries({ queryKey: ["login"] });
      if (data.status === 403) {
        setError("apiError", {
          type: "custom",
          message: translation.error_account_already_exists || "An account already exists for this email.",
        });
      } else {
        setError("apiError", {
          type: "custom",
          message: data.message || "An error occurred while logging in.",
        });
      }
    },
  });

  const {
    register,
    handleSubmit,
    // control,
    formState: { errors },
    // getValues,
    // setValue,
    setError,
    clearErrors,
  } = useForm<FormValues>();

  const requiredFieldMessage = translation.error_required_field || "Required field";
  const passwordTooShortMessage = translation.error_password_too_short || "Password must be at least 8 characters.";
  const inputErrorClass = "border-rose-500 focus:border-rose-500 focus:ring-rose-200";

  const registerHandleSubmit = (form_data: FormValues) => {
    clearErrors(["apiError", "email", "password", "first_name", "last_name", "confirmpassword"]);
    if (form_data.password !== form_data.confirmpassword) {
      setError("confirmpassword", {
        type: "manual",
        message: translation.error_passwords_must_match || "The passwords must match.",
      });
      return;
    }

    if (import.meta.env.VITE_PUBLIC_DEV === "true") {
      console.log(form_data);
      console.log("Time Zone Country Code:", countryCode);
      console.log("Browser Language:", userBrowserLanguage());
    }
    const payload: sso["schemas"]["RegisterFormModel"] = {
      email: form_data.email,
      password_input: form_data.password,
      country: countryCode,
      language: userBrowserLanguageChar2(),
      first_name: form_data.first_name,
      last_name: form_data.last_name,
      phone_number: "",
      suppress_mailchimp_update: false,
      marketing_opt_in: form_data.marketing_opt_in || false,
    };
    mutationRegister.mutate(payload);
  };

  return (
    <div className="font-inter-24">
      <LoginBox>
        <header className="flex flex-col">
          <Header1>{translation.registration_title || "Sign up to shop and manage your account"}</Header1>
        </header>
        <div className="flex flex-col gap-5">
          {errors.apiError && (
            <div className="mb-5 text-sm text-rose-600 bg-rose-50 py-3 px-4 rounded-md font-medium">
              {typeof errors.apiError.message === "string"
                ? errors.apiError.message
                : "An error occurred while logging in."}
            </div>
          )}
          {AppId === "GUMPlaybrush" && (
            <>
              {/* <div className={notificationYellowClass}>
                {translation.app_login_information ||
                  "To sign in, please use the same email address you used for your webshop order."}
              </div>
              <div className={notificationGreenClass}>
                {translation.app_login_information ||
                  "To sign in, please use the same email address you used for your webshop order."}
              </div> */}
              <div className={notificationGrayClass}>
                {translation.app_login_information ||
                  "To sign in, please use the same email address you used for your webshop order."}
              </div>
              {/* <div className={notificationRedClass}>
                {translation.app_login_information ||
                  "To sign in, please use the same email address you used for your webshop order."}
              </div> */}
            </>
          )}
          <form onSubmit={handleSubmit(registerHandleSubmit)}>
            <div className="flex flex-col gap-5">
              <input type="hidden" {...register("apiError")} />
              <div className="flex flex-col gap-1">
                <Input
                  {...register("email", { required: requiredFieldMessage })}
                  type="email"
                  label={translation.field_email_label || "Email"}
                  placeholder={translation.field_email_placeholder || "ex: john_doe or john_doe@domain.com"}
                  name="email"
                  className={errors.email ? inputErrorClass : undefined}
                />
                {errors.email && (
                  <p className="text-sm text-rose-600 px-1">
                    {typeof errors.email.message === "string" ? errors.email.message : requiredFieldMessage}
                  </p>
                )}
              </div>

              <div className="flex gap-4">
                <div className="flex-1 flex flex-col gap-1">
                  <Input
                    {...register("first_name", { required: requiredFieldMessage })}
                    type="text"
                    label={translation.field_first_name_label || "First Name"}
                    placeholder={translation.field_first_name_placeholder || "ex: John"}
                    name="first_name"
                    className={errors.first_name ? inputErrorClass : undefined}
                  />
                  {errors.first_name && (
                    <p className="text-sm text-rose-600 px-1">
                      {typeof errors.first_name.message === "string" ? errors.first_name.message : requiredFieldMessage}
                    </p>
                  )}
                </div>
                <div className="flex-1 flex flex-col gap-1">
                  <Input
                    {...register("last_name", { required: requiredFieldMessage })}
                    type="text"
                    label={translation.field_last_name_label || "Last Name"}
                    placeholder={translation.field_last_name_placeholder || "ex: Doe"}
                    name="last_name"
                    className={errors.last_name ? inputErrorClass : undefined}
                  />
                  {errors.last_name && (
                    <p className="text-sm text-rose-600 px-1">
                      {typeof errors.last_name.message === "string" ? errors.last_name.message : requiredFieldMessage}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex flex-col gap-1">
                <InputPassword
                  {...register("password", {
                    required: requiredFieldMessage,
                    minLength: { value: 8, message: passwordTooShortMessage },
                  })}
                  type="text"
                  label={translation.field_password_label || "Password"}
                  placeholder={translation.field_password_placeholder || "*************"}
                  name="password"
                  autoComplete="new-password"
                  className={errors.password ? inputErrorClass : undefined}
                />
                {errors.password && (
                  <p className="text-sm text-rose-600 px-1">
                    {typeof errors.password.message === "string" ? errors.password.message : requiredFieldMessage}
                  </p>
                )}
              </div>
              <div className="flex flex-col gap-1">
                <InputPassword
                  {...register("confirmpassword", {
                    required: requiredFieldMessage,
                    minLength: { value: 8, message: passwordTooShortMessage },
                  })}
                  type="password"
                  label={translation.field_confirmpassword_label || "Confirm Password"}
                  placeholder={translation.field_confirmpassword_placeholder || "ex: P@ssw0rd!123"}
                  name="confirmpassword"
                  autoComplete="new-password"
                  className={errors.confirmpassword ? inputErrorClass : undefined}
                />
                {errors.confirmpassword && (
                  <p className="text-sm text-rose-600 px-1">
                    {typeof errors.confirmpassword.message === "string"
                      ? errors.confirmpassword.message
                      : requiredFieldMessage}
                  </p>
                )}
              </div>
              <InputCheckbox
                {...register("marketing_opt_in")}
                name="marketing_opt_in"
                defaultChecked={true}
                label={translation.field_marketing_opt_in_label || "I agree to receive marketing communications."}
              />
            </div>
            <div>
              <P className="py-5">
                {translation.you_aggree_part1 || "By signing up, you agree to our"}
                <Link
                  className="text-[12px]"
                  href={
                    translation.terms_and_conditions_link || "https://www.sunstargum.com/gb-en/legal/terms-of-use.html"
                  }
                >
                  {translation.terms_and_conditions_text || "Terms"}
                </Link>
                {translation.you_aggree_part2 || ". Learn how we collect, use and share your data in our"}
                <Link
                  className="text-[12px]"
                  href={translation.privacy_policy_link || "https://www.sunstargum.com/gb-en/legal/privacy-policy.html"}
                >
                  {translation.privacy_policy_text || "Privacy Policy"}
                </Link>
                {translation.you_aggree_part3 || "."}
              </P>
            </div>
            <Button type="submit" disabled={mutationRegister.isPending}>
              <Icon
                icon={mutationRegister.isPending ? "prime:spinner" : "prime:user-plus"}
                className={mutationRegister.isPending ? "text-2xl mr-1 animate-spin" : "text-4xl mr-1 fill-white"}
              />
              {mutationRegister.isPending
                ? `${translation.button_sign_up || "Sign up"}...`
                : translation.button_sign_up || "Sign up"}
            </Button>
          </form>
          <div>
            {successfullNotification && (
              <div className={notificationGreenClass}>
                {translation.notification_confirm_email || "Please verify your email address to proceed with login."}
              </div>
            )}
          </div>
          {/* <div className="flex flex-col gap-5">
            <div className="flex gap-3 ">
              <div className="text-xs font-medium flex gap-1 items-center text-slate-400 ">
                <Icon icon="lets-icons:close-round" className="text-lg" />8 characters
              </div>
              <div className="text-xs font-medium flex gap-1 items-center text-slate-400 ">
                <Icon icon="lets-icons:close-round" className="text-lg" />1 special character
              </div>
              <div className="text-xs font-medium flex gap-1 items-center text-slate-400 ">
                <Icon icon="lets-icons:close-round" className="text-lg" />1 number
              </div>
            </div>
          </div> */}
        </div>
        <footer className="text-center text-sm text-slate-600 mt-7">
          {translation.already_have_an_account || "Already have an account?"}

          <Link className="pl-3" href="/auth/login">
            {translation.link_login_here || "Login here"}
          </Link>
        </footer>
      </LoginBox>
    </div>
  );
}
export default AuthRegister;
